import { useApolloClient } from '@apollo/client';
import { useMonogramCreateJsonDataContainerMutation, MonogramJsonDataContainerByIdDocument } from './useCreatePersonalizedMonogramContainer.graphql.generated';
import type { MonogramJsonDataContainerByIdQuery, MonogramJsonDataContainerByIdQueryVariables } from './useCreatePersonalizedMonogramContainer.graphql.generated';
import { personalizeMonogramContent } from '@shared/utils/monogram/monogram.utils';
import { stripMonogramPatternFields } from '@shared/websiteTemplates/monogramPattern.utils';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { MONOGRAM_JSON_CONTAINER_SCHEMA_URL } from './monogram.constants';
import { addError } from '@shared/utils/logger';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';

type CreatePersonalizedMonogramArgs = Readonly<{
  eventId: string;
  ownerFirstName: string | null | undefined;
  fianceeFirstName: string | null | undefined;
  // When omitted/null, the monogram's date layers are left untouched (initials-only personalization).
  eventDateMs?: number | null;
}>;

/**
 * Clones a monogram template's JSON container into a new per-event container, personalizing
 * it with the couple's initials (and event date). Returns the new container id, or null when the
 * source container is missing/empty. Shared by the website designer (template apply) and the
 * create-wedding funnel — takes eventId + names explicitly rather than reading event context, since
 * the create-wedding caller runs immediately after the event is created (before context reflects it).
 */
export const useCreatePersonalizedMonogramContainer = () => {
  // client.query (not useLazyQuery): on Apollo Client 3.4 the lazy execute resolves to void, so awaiting
  // it and destructuring `data` throws. client.query reliably resolves to a { data } result.
  // batchMode is carried as a variable, matching the monkey-patch convention in graphql/react-hooks.
  const client = useApolloClient();
  const [createJsonDataContainer] = useMonogramCreateJsonDataContainerMutation();
  const { toast } = useToast();
  const { tq } = useTranslation('websiteDesigner');

  const createMonogramFromJsonContainerId = useEventCallback(async (jsonContainerId: string | null | undefined, args: CreatePersonalizedMonogramArgs): Promise<string | null> => {
    const { eventId, ownerFirstName, fianceeFirstName, eventDateMs } = args;
    if (!jsonContainerId || !eventId) return null;

    // Prefer the bundled template content (covers all static monogram templates) to avoid a network
    // round-trip; lazy-imported so the ~52KB map stays out of the create-wedding critical bundle.
    // Fall back to the network query for any id not present inline (e.g. custom/experiment ids).
    let rawContent: Record<string, unknown> | undefined = (await import('@shared/websiteTemplates/data/wedding/monogram.inlineData')).MONOGRAM_INLINE_DATA[jsonContainerId] as
      | Record<string, unknown>
      | undefined;

    const usedNetworkFallback = !rawContent;
    if (usedNetworkFallback) {
      const { data: jsonContainerData } = await client.query<MonogramJsonDataContainerByIdQuery, MonogramJsonDataContainerByIdQueryVariables>({
        query: MonogramJsonDataContainerByIdDocument,
        variables: { payload: { id: jsonContainerId }, batchMode: 'fast' } as MonogramJsonDataContainerByIdQueryVariables,
        fetchPolicy: 'network-only',
      });
      rawContent = jsonContainerData?.jsonDataContainerById?.content;
    }

    if (!rawContent) return null;

    // Network-fallback content has no `pattern` metadata (authored only on inline templates), so
    // pattern-based personalization is a no-op — the monogram saves with template placeholder text.
    // Surface this rather than letting it pass silently; every personalizable template should be inline.
    if (usedNetworkFallback) {
      addError(new Error('Monogram personalization skipped: container id not in inline template map'), {
        context: 'useCreatePersonalizedMonogramContainer#networkFallback',
        jsonContainerId,
      });
      toast(tq`We couldn't personalize this monogram.`);
    }

    // Strip the authoring-only `pattern` field (present on inline template data) so it never reaches
    // the backend container.
    const content = stripMonogramPatternFields(personalizeMonogramContent(rawContent, ownerFirstName, fianceeFirstName, eventDateMs ?? undefined));

    const { data } = await createJsonDataContainer({
      variables: {
        payload: {
          type: 'monogram',
          content,
          eventId,
          schemaUrl: MONOGRAM_JSON_CONTAINER_SCHEMA_URL,
        },
      },
    });

    return data?.createJsonDataContainer?.id || null;
  });

  return { createMonogramFromJsonContainerId };
};
